// IntradayBoost.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './IntradayBoost.css';
import WatchlistStar from './WatchlistStar';
import RefreshButton from './RefreshButton';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement
} from 'chart.js';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement);

const IntradayBoost = ({ onBack }) => {
  const [boostData, setBoostData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('HIGH'); // 'HIGH', 'LOW'
  const [sliderValue, setSliderValue] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isMarketOpen, setIsMarketOpen] = useState(false);
  
  // Market hours: 9:15 AM to 3:30 PM (IST)
  const MARKET_START_HOUR = 9;
  const MARKET_START_MINUTE = 15;
  const MARKET_END_HOUR = 15;
  const MARKET_END_MINUTE = 30;

  // Convert time to minutes from market start
  const timeToMinutes = useCallback((hour, minute) => {
    const marketStartMinutes = MARKET_START_HOUR * 60 + MARKET_START_MINUTE;
    const currentMinutes = hour * 60 + minute;
    return Math.max(0, currentMinutes - marketStartMinutes);
  }, []);

  // Convert minutes back to time
  const minutesToTime = useCallback((minutes) => {
    const marketStartMinutes = MARKET_START_HOUR * 60 + MARKET_START_MINUTE;
    const totalMinutes = marketStartMinutes + minutes;
    return {
      hour: Math.floor(totalMinutes / 60),
      minute: totalMinutes % 60
    };
  }, []);

  // Check if market is open
  const checkMarketStatus = useCallback(() => {
    const now = new Date();
    const currentMinutesFromStart = timeToMinutes(now.getHours(), now.getMinutes());
    const marketEndMinutes = timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE);
    return currentMinutesFromStart >= 0 && currentMinutesFromStart <= marketEndMinutes;
  }, [timeToMinutes]);

  // Get maximum slider value
  const getMaxSliderValue = useCallback(() => {
    const marketEndMinutes = timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE);
    if (isMarketOpen) {
      const currentMinutesFromStart = timeToMinutes(currentTime.getHours(), currentTime.getMinutes());
      return Math.min(currentMinutesFromStart, marketEndMinutes);
    }
    return marketEndMinutes;
  }, [isMarketOpen, currentTime, timeToMinutes]);

  const fetchBoostData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/intraday/scan`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      if (json.success) {
        const rows = Array.isArray(json.data) ? json.data : [];
        setBoostData(rows);
      } else {
        setBoostData([]);
        setError(json.message || 'No data found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load data.');
      setBoostData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoostData();
  }, [fetchBoostData]);

  // Update current time and market status
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now);
      setIsMarketOpen(checkMarketStatus());
      
      if (checkMarketStatus()) {
        const currentMinutesFromStart = timeToMinutes(now.getHours(), now.getMinutes());
        setSliderValue(Math.max(0, currentMinutesFromStart));
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [checkMarketStatus, timeToMinutes]);

  // Handle slider change
  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    const maxValue = getMaxSliderValue();
    const clampedValue = Math.min(value, maxValue);
    setSliderValue(clampedValue);
  };

  // Filter data based on selected filter type
  const filteredData = boostData.filter(item => {
    const pct = Number(item.percent_change);
    if (filterType === 'HIGH') return pct > 0;
    if (filterType === 'LOW') return pct < 0;
    return true;
  });

  return (
    <div className="intraday-page">
      <header className="intraday-header">
        <div className="intraday-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
          <div className="intraday-user">
            <RefreshButton 
              onRefresh={fetchBoostData} 
              isLoading={loading}
              size="small"
              style={{ marginRight: '10px' }}
            />
            <button onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      </header>

      <main className="intraday-main">
        <div className="intraday-top">
          <h1 className="page-title">Intraday Boost <span className="info-icon">ℹ️</span></h1>
          
          {/* Filter Buttons */}
          <div className="filter-buttons-container">
            <button 
              className={`filter-btn ${filterType === 'HIGH' ? 'active' : ''}`}
              onClick={() => setFilterType('HIGH')}
            >
              HIGH
            </button>
            <button 
              className={`filter-btn ${filterType === 'LOW' ? 'active' : ''}`}
              onClick={() => setFilterType('LOW')}
            >
              LOW
            </button>
          </div>
        </div>

        {/* Time Slider */}
        <div className="time-slider-container">
          <div className="time-slider">
            <div className="slider-header">
              <h3>Time Slider - View Historical Intraday Data</h3>
              <p className="slider-description">
                Market Hours: 9:15 AM - 3:30 PM | 
                Status: <span className={isMarketOpen ? 'market-open' : 'market-closed'}>
                  {isMarketOpen ? 'Open' : 'Closed'}
                </span>
              </p>
            </div>
            <div className="slider-track">
              <div 
                className="slider-progress" 
                style={{ 
                  width: `${(sliderValue / timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE)) * 100}%`
                }}
              />
              <input
                type="range"
                min="0"
                max={getMaxSliderValue()}
                value={sliderValue}
                onChange={handleSliderChange}
                className="time-range-slider"
              />
            </div>
            <div className="time-labels">
              <span>9:15 AM</span>
              <span className="selected-time">
                Selected: {(() => {
                  const { hour, minute } = minutesToTime(sliderValue);
                  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                })()}
              </span>
              <span>3:30 PM</span>
            </div>
          </div>
        </div>

        <div className="intraday-table-container">
          {loading ? (
            <div className="loading">Loading data...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <div className="intraday-table">
              <div className="table-header">
                <div>Rank</div>
                <div>Symbol</div>
                <div>LTP</div>
                <div>Percent Change</div>
                <div>Z Score</div>
                <div>SIGNALS</div>
              </div>

              {filteredData.map((item, idx) => {
                const pct = Number(item.percent_change);
                const avg = Number(item['recent_avg_daily_growth (%)']);
                const z = Number(item.z_score);
                const ltp = item.ltp;
                const isPositive = pct >= 0;

                return (
                  <div className="table-row--intraday" key={`${item.stock}-${idx}`}>
                    <div>{idx + 1}</div>
                    <div>
                      <WatchlistStar 
                        stock={{
                          symbol: item.stock,
                          name: item.stock
                        }}
                      />
                      {item.stock}
                    </div>
                    <div>{ltp ?? '—'}</div>
                    <div className={isPositive ? 'positive' : 'negative'}>
                      {Number.isFinite(pct) ? `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%` : '—'}
                    </div>
                    <div>{Number.isFinite(z) ? z.toFixed(2) : '—'}</div>
                    <div className="chart-cell" style={{ color: isPositive ? '#10b981' : '#ef4444', fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {isPositive ? '↑' : '↓'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default IntradayBoost;
